
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import CountryFlag from "@/components/country/CountryFlag";

export function VerificationRulesTab() {
  const [countries, setCountries] = useState<any[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [rules, setRules] = useState<any>({
    requires_primary_document: true,
    requires_residency_document: false,
    expatriate_requires_passport: true,
    expatriate_requires_residency: true
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    loadCountries();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      loadRules(selectedCountry);
    }
  }, [selectedCountry]);

  const loadCountries = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('countries')
        .select('code, name')
        .eq('active', true)
        .order('name');
        
      if (error) throw error;
      
      setCountries(data || []);
    } catch (error: any) {
      console.error('Error loading countries:', error);
      toast.error(`Failed to load countries: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRules = async (countryCode: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('verification_rules')
        .select('*')
        .eq('country_code', countryCode)
        .single();
        
      if (error && error.code !== 'PGRST116') { // PGRST116 is not found
        throw error;
      }
      
      if (data) {
        setRules(data);
      } else {
        // Set defaults for new country
        setRules({
          requires_primary_document: true,
          requires_residency_document: false,
          expatriate_requires_passport: true,
          expatriate_requires_residency: true,
          country_code: countryCode
        });
      }
    } catch (error: any) {
      console.error('Error loading verification rules:', error);
      toast.error(`Failed to load rules: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveRules = async () => {
    if (!selectedCountry) {
      toast.error('Please select a country');
      return;
    }

    setIsSaving(true);
    try {
      // Check if rules exist for this country
      const { data: existingRule, error: checkError } = await supabase
        .from('verification_rules')
        .select('id')
        .eq('country_code', selectedCountry)
        .maybeSingle();
        
      if (checkError) throw checkError;
      
      if (existingRule) {
        // Update existing rules
        const { error } = await supabase
          .from('verification_rules')
          .update({
            requires_primary_document: rules.requires_primary_document,
            requires_residency_document: rules.requires_residency_document,
            expatriate_requires_passport: rules.expatriate_requires_passport,
            expatriate_requires_residency: rules.expatriate_requires_residency,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingRule.id);
          
        if (error) throw error;
      } else {
        // Insert new rules
        const { error } = await supabase
          .from('verification_rules')
          .insert({
            country_code: selectedCountry,
            requires_primary_document: rules.requires_primary_document,
            requires_residency_document: rules.requires_residency_document,
            expatriate_requires_passport: rules.expatriate_requires_passport,
            expatriate_requires_residency: rules.expatriate_requires_residency
          });
          
        if (error) throw error;
      }
      
      toast.success('Verification rules updated successfully');
    } catch (error: any) {
      console.error('Error saving verification rules:', error);
      toast.error(`Failed to save rules: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h2 className="text-xl md:text-2xl font-bold tracking-tight">Verification Rules</h2>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Country Rules Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="country">Select Country</Label>
              <Select
                value={selectedCountry}
                onValueChange={setSelectedCountry}
                disabled={isLoading}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map(country => (
                    <SelectItem key={country.code} value={country.code}>
                      <div className="flex items-center gap-2">
                        <CountryFlag countryCode={country.code} className="h-4 w-5" />
                        <span>{country.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-sm text-muted-foreground">
                Configure verification rules specific to each country
              </p>
            </div>
          </div>

          {selectedCountry && (
            <>
              <div className="space-y-4 border-t pt-4">
                <h3 className="font-medium">Document Requirements</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="requires_primary" 
                        checked={rules.requires_primary_document}
                        onCheckedChange={(checked) => setRules({...rules, requires_primary_document: checked})}
                      />
                      <Label htmlFor="requires_primary" className="cursor-pointer">
                        Require primary document
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground pl-6">
                      Primary documents include national ID, passport, or driver's license
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="requires_residency" 
                        checked={rules.requires_residency_document}
                        onCheckedChange={(checked) => setRules({...rules, requires_residency_document: checked})}
                      />
                      <Label htmlFor="requires_residency" className="cursor-pointer">
                        Require residency document
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground pl-6">
                      Residency documents include utility bills, lease agreements, etc.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4 border-t pt-4">
                <h3 className="font-medium">Expatriate Requirements</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="requires_passport" 
                        checked={rules.expatriate_requires_passport}
                        onCheckedChange={(checked) => setRules({...rules, expatriate_requires_passport: checked})}
                      />
                      <Label htmlFor="requires_passport" className="cursor-pointer">
                        Expatriates must provide passport
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground pl-6">
                      Require expatriates to upload a passport from their country of citizenship
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="requires_expat_residency" 
                        checked={rules.expatriate_requires_residency}
                        onCheckedChange={(checked) => setRules({...rules, expatriate_requires_residency: checked})}
                      />
                      <Label htmlFor="requires_expat_residency" className="cursor-pointer">
                        Expatriates must prove residency
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground pl-6">
                      Require expatriates to provide proof of legal residency (work permit, visa, etc.)
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleSaveRules} disabled={isSaving || isLoading}>
                  {isSaving ? "Saving..." : "Save Rules"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
